import React, { useMemo } from 'react';
import { ClipboardList, AlertTriangle, TrendingUp, Eye, GitCompare } from 'lucide-react';
import { ValidationProject } from '../../types/validation';

interface Stage1ViewProps {
  projects: ValidationProject[];
  onViewProject: (projectId: string) => void;
  onViewComparison: (projectId: string) => void;
  onProjectUpdate?: () => void;
}

const Stage1View: React.FC<Stage1ViewProps> = ({ projects, onViewProject, onViewComparison }) => {
  const stage1Projects = useMemo(() => projects.filter(p => p.status === 'stage1'), [projects]);

  // Memoize flag type counts
  const { missingFieldsCount, requirementCount, materialChangeCount } = useMemo(() => ({
    missingFieldsCount: projects.filter(p =>
      p.flags.some(f => f.type === 'Missing Required Fields')
    ).length,
    requirementCount: projects.filter(p =>
      p.flags.some(f => f.type === 'Requirement Adherence Issue')
    ).length,
    materialChangeCount: projects.filter(p =>
      p.flags.some(f => f.type === 'Material Change vs Previous Year')
    ).length,
  }), [projects]);

  const flagTypes = [
    { icon: <ClipboardList className="w-6 h-6" />, title: 'Missing Required Fields', desc: 'Essential data not provided', count: missingFieldsCount },
    { icon: <AlertTriangle className="w-6 h-6" />, title: 'Requirement Adherence', desc: 'Does not meet VWBA criteria', count: requirementCount },
    { icon: <TrendingUp className="w-6 h-6" />, title: 'Material Change (Historic)', desc: 'Significant YoY deviation', count: materialChangeCount },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Stage 1 - Automated Filtering</h2>
        <p className="text-gray-600 mt-1">Projects flagged during initial automated validation. Stage 1 filters for completeness, consistency, and adherence to requirements.</p>
      </div>

      {/* Flag Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {flagTypes.map((flag, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="text-amber-500 mb-2">{flag.icon}</div>
            <h4 className="font-semibold text-gray-900 text-sm">{flag.title}</h4>
            <p className="text-xs text-gray-500 mt-1">{flag.desc}</p>
            <span className="inline-block mt-2 px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
              {flag.count} projects
            </span>
          </div>
        ))}
      </div>

      {/* Flagged Projects Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Flagged Projects - Stage 1 ({stage1Projects.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" aria-label="Stage 1 flagged projects">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project ID</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flag Reason</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Basin</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {stage1Projects.map(project => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{project.id}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{project.name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      project.type === 'new' ? 'bg-cyan-100 text-cyan-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {project.type === 'new' ? 'New' : 'Historic'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {project.flags.map(f => f.type).join(', ')}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{project.details.basin}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => onViewProject(project.id)}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1"
                        aria-label={`View ${project.name}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        View
                      </button>
                      <button
                        onClick={() => onViewComparison(project.id)}
                        className="px-3 py-1.5 text-sm font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors flex items-center gap-1"
                        aria-label={`Compare ${project.name}`}
                      >
                        <GitCompare className="w-3.5 h-3.5" />
                        Compare
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {stage1Projects.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No projects flagged in Stage 1
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Stage1View;
